import { useEffect, useState } from "react";

import { useFormik } from "formik";
import i18next from "i18next";
import moment from "moment";
import * as Yup from "yup";

import { formatDateTime } from "../../../config";
import { getOrders } from "../../../protocols/rest/getOrders";
import { supportTicketCreate } from "../../../protocols/rest/support";
import {
  getCategories,
  getServiceTypes,
  supportCategoryLabel,
  supportErrorText,
  supportServiceLabel,
  validateSupportFiles,
} from "../utils";

import { DotsLoadingComponent, InputComponent, TextareaComponent } from "../../ui";
import SupportFilesInput from "../SupportFilesInput";
import SupportSelectField from "../SupportSelectField";

import {
  ButtonLoadingCover,
  Buttons,
  Card,
  CardTitle,
  Fields,
  FormError,
  Loading,
  PrimaryButton,
  ReadonlyField,
  Row,
  SecondaryButton,
} from "../style";

const MANUAL_ORDER = "__manual__";
const DEFAULT_SERVICE_TYPE = "avia";

// Подпись заказа в выпадающем списке: номер, маршрут и дата вылета (если справочники пришли)
const orderLabel = (order, included, language) => {
  const routes = (order?.routes || [])
    .map(route => {
      const city = code => {
        const cityCode = included?.airport?.[code]?.city_code;
        return included?.city?.[cityCode]?.[language] || code;
      };

      return `${city(route?.departure_airport_code)} → ${city(route?.arrival_airport_code)}`;
    })
    .join(", ");

  const departure = order?.routes?.[0]?.departure_at;
  const date = departure ? moment(departure, formatDateTime).format("DD.MM.YYYY") : "";

  return [order.order_id, routes, date].filter(Boolean).join(" · ");
};

/**
 * Новая заявка. Залогиненный клиент может выбрать свой заказ, ввести номер или не указывать заказ;
 * гость пишет только по заказу, к которому подтвердил доступ (order_id подставит бэкенд).
 */
const SupportTicketCreateForm = ({ meta, isGuest = false, guestOrderId = null, presetOrderId = "", onCreated, onCancel }) => {
  const { t, language } = i18next;

  const [orders, setOrders] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesError, setFilesError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const limits = meta?.limits || {};
  const serviceTypes = getServiceTypes(meta);
  const defaultServiceType = serviceTypes.includes(DEFAULT_SERVICE_TYPE) ? DEFAULT_SERVICE_TYPE : serviceTypes[0] || "";

  // свои заказы для выбора (только для залогиненного)
  useEffect(() => {
    if (isGuest) return;

    let isActive = true;

    getOrders()
      .then(result => {
        if (!isActive) return;

        const data = result?.data;
        const list = Array.isArray(data?.data) ? data.data : [];

        setOrders(list.map(order => ({ value: String(order.order_id), label: orderLabel(order, data?.included, language) })));
      })
      .catch(error => {
        console.log({ supportOrdersError: error });
        if (isActive) setOrders([]);
      });

    return () => {
      isActive = false;
    };
  }, [isGuest, language]);

  const subjectMax = Number(limits.subject_max) || 200;
  const messageMax = Number(limits.message_max) || 10000;

  const validationSchema = Yup.object().shape({
    subject: Yup.string()
      .trim()
      .required(t("required"))
      .max(subjectMax, t("support_text_too_long", { max: subjectMax })),
    category: Yup.string().required(t("required")),
    message: Yup.string()
      .trim()
      .required(t("required"))
      .max(messageMax, t("support_text_too_long", { max: messageMax })),
    order_choice: Yup.string(),
    order_manual: Yup.string().when("order_choice", {
      is: MANUAL_ORDER,
      then: schema => schema.trim().required(t("required")),
      otherwise: schema => schema,
    }),
  });

  const handleFormSubmit = async formValues => {
    if (isSubmitting) return;

    const fileError = validateSupportFiles(files, limits);
    setFilesError(fileError);
    if (fileError) return;

    let orderId = null;
    if (!isGuest) {
      orderId = formValues.order_choice === MANUAL_ORDER ? formValues.order_manual.trim() : formValues.order_choice || null;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const data = await supportTicketCreate({
        subject: formValues.subject.trim(),
        message: formValues.message.trim(),
        category: formValues.category,
        service_type: formValues.service_type || null,
        order_id: orderId,
        files,
      });

      onCreated(data?.ticket);
    } catch (error) {
      if (error?.code === 71 || error?.key === "support_order_not_found") {
        setFieldError(formValues.order_choice === MANUAL_ORDER ? "order_manual" : "order_choice", supportErrorText(error));
      } else if (error?.code === 70 || error?.key === "support_file_invalid") {
        setFilesError(supportErrorText(error));
      } else {
        setFormError(supportErrorText(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, setFieldError } =
    useFormik({
      initialValues: {
        service_type: defaultServiceType,
        category: "",
        subject: "",
        message: "",
        order_choice: presetOrderId ? MANUAL_ORDER : "",
        order_manual: presetOrderId,
      },
      validationSchema,
      onSubmit: handleFormSubmit,
      validateOnBlur: true,
    });

  // meta пришла позже формы — ставим тип услуги по умолчанию
  useEffect(() => {
    if (!values.service_type && defaultServiceType) setFieldValue("service_type", defaultServiceType, false);
  }, [defaultServiceType, values.service_type, setFieldValue]);

  // заказ из ссылки нашёлся в списке своих заказов — выбираем его
  useEffect(() => {
    if (presetOrderId && orders?.some(order => order.value === String(presetOrderId))) {
      setFieldValue("order_choice", String(presetOrderId), false);
    }
  }, [orders, presetOrderId, setFieldValue]);

  const categoryOptions = getCategories(meta, values.service_type).map(category => ({
    value: category,
    label: supportCategoryLabel(category),
  }));

  const orderOptions = [...(orders || []), { value: MANUAL_ORDER, label: t("support_order_manual") }];

  if (!meta) {
    return (
      <Card>
        <Loading>
          <DotsLoadingComponent />
        </Loading>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>{t("support_new_ticket")}</CardTitle>

      <Fields>
        {isGuest ? (
          <ReadonlyField>
            <span>{t("support_order")}</span>
            <b>{guestOrderId}</b>
          </ReadonlyField>
        ) : (
          <Row>
            <SupportSelectField
              label={t("support_order_optional")}
              value={values.order_choice}
              onChange={value => setFieldValue("order_choice", value)}
              options={orderOptions}
              placeholder={orders === null ? "…" : t("support_order_none")}
              touched={touched.order_choice}
              error={errors.order_choice}
            />
            {values.order_choice === MANUAL_ORDER && (
              <InputComponent
                value={values.order_manual}
                onChange={handleChange("order_manual")}
                onBlur={handleBlur("order_manual")}
                label={t("support_order_number")}
                placeholder={t("support_enter_order_number")}
                touched={touched.order_manual}
                error={errors.order_manual}
                required
              />
            )}
          </Row>
        )}

        <Row>
          {serviceTypes.length > 1 && (
            <SupportSelectField
              label={t("support_service_type")}
              value={values.service_type}
              onChange={value => {
                setFieldValue("service_type", value);
                setFieldValue("category", "");
              }}
              options={serviceTypes.map(type => ({ value: type, label: supportServiceLabel(type) }))}
              required
            />
          )}
          <SupportSelectField
            label={t("support_category")}
            value={values.category}
            onChange={value => {
              setFieldValue("category", value);
              setFieldTouched("category", true, false);
            }}
            options={categoryOptions}
            placeholder={t("support_category_choose")}
            touched={touched.category}
            error={errors.category}
            required
          />
        </Row>

        <InputComponent
          value={values.subject}
          onChange={handleChange("subject")}
          onBlur={handleBlur("subject")}
          label={t("support_subject")}
          placeholder={t("support_subject_placeholder")}
          touched={touched.subject}
          error={errors.subject}
          maxLength={subjectMax}
          required
        />

        <TextareaComponent
          value={values.message}
          onChange={handleChange("message")}
          onBlur={handleBlur("message")}
          label={t("support_message")}
          placeholder={t("support_message_placeholder")}
          touched={touched.message}
          error={errors.message}
          rows={6}
          maxLength={messageMax}
          required
        />

        <SupportFilesInput
          files={files}
          onChange={nextFiles => {
            setFiles(nextFiles);
            setFilesError(validateSupportFiles(nextFiles, limits));
          }}
          limits={limits}
          error={filesError}
          disabled={isSubmitting}
        />
      </Fields>

      {formError && <FormError>{formError}</FormError>}

      <Buttons>
        <PrimaryButton type="button" onClick={handleSubmit} disabled={isSubmitting}>
          {t("support_create")}
          {isSubmitting && (
            <ButtonLoadingCover>
              <DotsLoadingComponent color="#fff" />
            </ButtonLoadingCover>
          )}
        </PrimaryButton>
        {onCancel && (
          <SecondaryButton type="button" onClick={onCancel} disabled={isSubmitting}>
            {t("support_cancel")}
          </SecondaryButton>
        )}
      </Buttons>
    </Card>
  );
};

export default SupportTicketCreateForm;
